export const START_PAGE_ITEM_TYPES = {
    CARD: "card",
    MENU: "menu",
    MENU_ICON: "menu-icon",
    MENU_ITEM: "menu-item",
};
let lastMouseEvent = null;
let mouseTrackerInstalled = false;
function updateMousePosition(mouse, clientX, clientY, mode) {
    if (mode === "fixed") {
        mouse.x = clientX;
        mouse.y = clientY;
        return;
    }
    mouse.x = clientX + window.scrollX;
    mouse.y = clientY + window.scrollY;
}
function rememberMouseEvent(event) {
    lastMouseEvent = {
        clientX: event.clientX,
        clientY: event.clientY,
    };
}
export function installStartPageMouseTracker() {
    if (mouseTrackerInstalled || typeof window === "undefined") {
        return;
    }
    mouseTrackerInstalled = true;
    window.addEventListener("mousemove", rememberMouseEvent, true);
    window.addEventListener("mousedown", rememberMouseEvent, true);
    window.addEventListener("click", rememberMouseEvent, true);
}
installStartPageMouseTracker();
const DEFAULT_TARGET_SOURCES = [
    {
        kind: START_PAGE_ITEM_TYPES.CARD,
        selector: '[data-whimsy-kind="card"]',
    },
    {
        kind: START_PAGE_ITEM_TYPES.MENU,
        selector: '[data-whimsy-kind="menu"]',
    },
    {
        kind: START_PAGE_ITEM_TYPES.MENU_ITEM,
        selector: '[data-whimsy-kind="menu-item"]',
    },
    {
        kind: START_PAGE_ITEM_TYPES.MENU_ICON,
        selector: '[data-whimsy-kind="menu-icon"]',
    },
];
export function createStartPage(whimsyCanvas) {
    let keyDownHandlers = [];
    let keyUpHandlers = [];
    function addHandler(handlers, handler) {
        handlers.push(handler);
        return () => {
            const index = handlers.indexOf(handler);
            if (index >= 0) {
                handlers.splice(index, 1);
            }
        };
    }
    function runHandlers(handlers, event) {
        for (let handler of handlers) {
            if (handler(event)) {
                return;
            }
        }
    }
    return {
        addKeyDownHandler: (handler) => addHandler(keyDownHandlers, handler),
        addKeyUpHandler: (handler) => addHandler(keyUpHandlers, handler),
        getItems: (kind) => whimsyCanvas.getTargets(kind),
        itemTypes: START_PAGE_ITEM_TYPES,
        keys: {},
        mouse: {
            x: 0,
            y: 0,
        },
        handleKeyDown(event) {
            this.keys[event.key] = true;
            runHandlers(keyDownHandlers, event);
        },
        handleKeyUp(event) {
            this.keys[event.key] = false;
            runHandlers(keyUpHandlers, event);
        },
        handleMouseMove(event, mode) {
            updateMousePosition(this.mouse, event.clientX, event.clientY, mode);
        },
        useLastMousePosition(mode) {
            if (!lastMouseEvent) {
                return;
            }
            updateMousePosition(this.mouse, lastMouseEvent.clientX, lastMouseEvent.clientY, mode);
        },
    };
}
function toRect(bounds) {
    return {
        left: bounds.left,
        top: bounds.top,
        right: bounds.right,
        bottom: bounds.bottom,
        width: bounds.width,
        height: bounds.height,
        centerX: bounds.left + bounds.width / 2,
        centerY: bounds.top + bounds.height / 2,
    };
}
function shiftRect(rect, deltaX, deltaY) {
    return {
        left: rect.left + deltaX,
        top: rect.top + deltaY,
        right: rect.right + deltaX,
        bottom: rect.bottom + deltaY,
        width: rect.width,
        height: rect.height,
        centerX: rect.centerX + deltaX,
        centerY: rect.centerY + deltaY,
    };
}
function normalizeDataset(dataset) {
    return Object.entries(dataset).reduce((record, [key, value]) => {
        if (value != null) {
            record[key] = value;
        }
        return record;
    }, {});
}
function getDocumentSize() {
    const body = document.body;
    const root = document.documentElement;
    const main = document.querySelector("main");
    const mainBounds = main ? main.getBoundingClientRect() : null;
    const mainBottom = mainBounds ? Math.ceil(mainBounds.bottom + window.scrollY) : 0;
    const viewportWidth = Math.max(root.clientWidth, (body === null || body === void 0 ? void 0 : body.clientWidth) || 0, mainBounds ? Math.ceil(mainBounds.width) : 0);
    return {
        width: viewportWidth,
        height: Math.max(window.innerHeight, root.clientHeight, root.scrollHeight, (body === null || body === void 0 ? void 0 : body.scrollHeight) || 0, mainBottom),
    };
}
function measureTarget(element, kind, id, mode) {
    const bounds = element.getBoundingClientRect();
    if (!bounds.width || !bounds.height) {
        return null;
    }
    const fixedRect = toRect(bounds);
    const absoluteRect = shiftRect(fixedRect, window.scrollX, window.scrollY);
    return {
        id,
        kind,
        element,
        rect: mode === "fixed" ? fixedRect : absoluteRect,
        fixedRect,
        absoluteRect,
        dataset: normalizeDataset(element.dataset),
    };
}
export function createWhimsyCanvas(options = {}) {
    const targetSources = [
        ...(options.autoTrackDefaultTargets === false ? [] : DEFAULT_TARGET_SOURCES),
        ...(options.targetSources || []),
    ];
    const useDevicePixelRatio = options.useDevicePixelRatio !== false;
    const zIndex = options.zIndex || 999999;
    let mode = options.mode || "absolute";
    let canvas = null;
    let ctx = null;
    let width = 0;
    let height = 0;
    let pixelRatio = Math.max(1, window.devicePixelRatio || 1);
    let targets = [];
    let destroyed = false;
    let refreshScheduled = false;
    let lastViewportWidth = 0;
    let lastViewportHeight = 0;
    let lastScrollX = 0;
    let lastScrollY = 0;
    const resizeObserver = typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            scheduleRefresh();
        });
    const mutationObserver = typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver(() => {
            scheduleRefresh();
        });
    function resolveId(source, element, index) {
        if (source.getId) {
            return source.getId(element, index);
        }
        return (element.dataset.whimsyId ||
            element.id ||
            `${source.kind}-${element.dataset.whimsyTitle || index}`);
    }
    function collectTargets() {
        const nextTargets = [];
        targetSources.forEach((source) => {
            const sourceElements = source.resolveElements
                ? Array.from(source.resolveElements())
                : Array.from(document.querySelectorAll(source.selector || ""));
            sourceElements.forEach((element, index) => {
                const measured = measureTarget(element, source.kind, resolveId(source, element, index), mode);
                if (!measured) {
                    return;
                }
                nextTargets.push({
                    id: measured.id,
                    kind: measured.kind,
                    element: measured.element,
                    fixedRect: measured.fixedRect,
                    absoluteRect: measured.absoluteRect,
                    dataset: measured.dataset,
                });
            });
        });
        targets = nextTargets;
    }
    function getViewportSnapshot() {
        return {
            width: mode === "fixed"
                ? document.documentElement.clientWidth
                : getDocumentSize().width,
            height: mode === "fixed" ? window.innerHeight : getDocumentSize().height,
            scrollX: window.scrollX,
            scrollY: window.scrollY,
        };
    }
    function syncViewportSnapshot() {
        const snapshot = getViewportSnapshot();
        lastViewportWidth = snapshot.width;
        lastViewportHeight = snapshot.height;
        lastScrollX = snapshot.scrollX;
        lastScrollY = snapshot.scrollY;
    }
    function viewportChanged() {
        const snapshot = getViewportSnapshot();
        return (snapshot.width !== lastViewportWidth ||
            snapshot.height !== lastViewportHeight ||
            snapshot.scrollX !== lastScrollX ||
            snapshot.scrollY !== lastScrollY);
    }
    function updateCanvasElement() {
        if (!canvas || !ctx) {
            return;
        }
        pixelRatio = useDevicePixelRatio
            ? Math.max(1, window.devicePixelRatio || 1)
            : 1;
        const nextSize = mode === "fixed"
            ? {
                width: document.documentElement.clientWidth,
                height: window.innerHeight,
            }
            : getDocumentSize();
        width = nextSize.width;
        height = nextSize.height;
        canvas.style.pointerEvents = "none";
        canvas.style.position = mode === "fixed" ? "fixed" : "absolute";
        canvas.style.left = "0";
        canvas.style.top = "0";
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        canvas.style.zIndex = `${zIndex}`;
        canvas.style.display = "block";
        canvas.setAttribute("aria-hidden", "true");
        const scaledWidth = Math.max(1, Math.round(width * pixelRatio));
        const scaledHeight = Math.max(1, Math.round(height * pixelRatio));
        if (canvas.width !== scaledWidth) {
            canvas.width = scaledWidth;
        }
        if (canvas.height !== scaledHeight) {
            canvas.height = scaledHeight;
        }
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        syncViewportSnapshot();
    }
    function refresh() {
        if (destroyed) {
            return;
        }
        updateCanvasElement();
        collectTargets();
    }
    function scheduleRefresh() {
        if (destroyed || refreshScheduled) {
            return;
        }
        refreshScheduled = true;
        window.requestAnimationFrame(() => {
            refreshScheduled = false;
            refresh();
        });
    }
    function getTargets(kind, targetMode = mode) {
        return targets
            .filter((target) => !kind || target.kind === kind)
            .map((target) => (Object.assign(Object.assign({}, target), { rect: targetMode === "fixed" ? target.fixedRect : target.absoluteRect })));
    }
    function attach(nextCanvas) {
        var _a, _b;
        canvas = nextCanvas;
        ctx = canvas.getContext("2d");
        if (!ctx) {
            throw new Error("Unable to create whimsy canvas context");
        }
        refresh();
        window.addEventListener("resize", scheduleRefresh);
        window.addEventListener("scroll", scheduleRefresh, true);
        (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.addEventListener("resize", scheduleRefresh);
        (_b = window.visualViewport) === null || _b === void 0 ? void 0 : _b.addEventListener("scroll", scheduleRefresh);
        if (resizeObserver) {
            resizeObserver.observe(document.body);
            const main = document.querySelector("main");
            if (main) {
                resizeObserver.observe(main);
            }
        }
        if (mutationObserver) {
            mutationObserver.observe(document.body, {
                subtree: true,
                childList: true,
                attributes: true,
                attributeFilter: ["class", "style", "hidden", "open", "data-whimsy-kind"],
            });
        }
    }
    function registerTargetSource(source) {
        targetSources.push(source);
        scheduleRefresh();
        return () => {
            const index = targetSources.indexOf(source);
            if (index >= 0) {
                targetSources.splice(index, 1);
                scheduleRefresh();
            }
        };
    }
    function setMode(nextMode) {
        mode = nextMode;
        refresh();
    }
    function destroy() {
        var _a, _b;
        if (destroyed) {
            return;
        }
        destroyed = true;
        resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
        mutationObserver === null || mutationObserver === void 0 ? void 0 : mutationObserver.disconnect();
        window.removeEventListener("resize", scheduleRefresh);
        window.removeEventListener("scroll", scheduleRefresh, true);
        (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.removeEventListener("resize", scheduleRefresh);
        (_b = window.visualViewport) === null || _b === void 0 ? void 0 : _b.removeEventListener("scroll", scheduleRefresh);
        canvas = null;
        ctx = null;
        targets = [];
    }
    return {
        attach,
        destroy,
        getCanvasSize: () => ({ width, height }),
        getMode: () => mode,
        getTargets,
        measureElement: (element, kind = "custom", id = element.dataset.whimsyId || element.id || kind, targetMode = mode) => measureTarget(element, kind, id, targetMode),
        refresh,
        registerTargetSource,
        setMode,
    };
}
